import React, { useState, useEffect, useCallback } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Collapse,
  Box,
  Typography,
  Link,
} from '@mui/material';
import theme from 'theme';
import { KeyboardArrowUp, KeyboardArrowDown, AccessTime, Done } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { BudgetCosts, InvoiceUpload } from 'components/BudgetCosts';
import { apiClient } from '../../api-client/index.js';
import { DarkTooltip } from '../../components/DarkTooltip';
import { formatDate, snapToArray, isHttpUrl } from '../../utils';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../components/Portlets';
import { Status } from '../../components/Status';
import { STATUS_COLORS } from '../../constants/requestStatus';
import { useAuth } from '../../auth';
import { useFirestore } from '../../utils/firebase';

const Row = props => {
  const {
    status,
    requestedAt,
    type,
    title,
    link,
    description,
    id,
    cancelRequest,
    totalAmount,
    requests,
    startDate,
    endDate,
    hideActionsColumnAndCell,
    budgetCosts,
  } = props;
  const [open, setOpen] = React.useState(false);
  const showAttachment =
    (status === 'approved' || status === 'completed' || status === 'canceled') &&
    !(type === 'Conference' || type === 'Workshop');

  return (
    <>
      <TableRow sx={{ boxShadow: 'none' }} data-cy={description} hover>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none', width: '1rem' }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Status
              sx={{ marginRight: theme.spacing(1) }}
              color={STATUS_COLORS[status]}
              size="sm"
            />
            {status}
          </div>
        </TableCell>

        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          {formatDate(requestedAt)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>{type}</TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(status === 'pending' || status === 'approved') && (
              <DarkTooltip
                title="Preliminary amount - may differ until the 
              request is completed"
              >
                <AccessTime
                  sx={{
                    marginRight: '0.5rem',
                    color: '#686868',
                    width: '1.2rem',
                    height: '1.2rem',
                  }}
                />
              </DarkTooltip>
            )}
            {status === 'completed' && (
              <Done
                sx={{ color: '#65B32E', marginRight: '0.5rem', width: '1.2rem', height: '1.2rem' }}
              />
            )}
            {totalAmount}
          </div>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>{title}</TableCell>
        {hideActionsColumnAndCell(requests) && (
          <TableCell sx={{ borderBottom: 'unset', boxShadow: 'none' }}>
            {status === 'canceled' || status === 'denied' || status === 'completed' ? (
              <div />
            ) : (
              <Button
                size="small"
                onClick={() => {
                  cancelRequest(id);
                }}
                sx={{
                  backgroundColor: theme.palette.danger.main,
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundColor: '#c62828',
                  },
                }}
                data-cy="cancelButton"
              >
                CANCEL
              </Button>
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow
        sx={{
          height: '0px',
          padding: 0,
          borderBottom: 'unset',
          borderTop: 'unset',
          boxShadow: 'none',
        }}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box
                sx={[
                  showAttachment
                    ? { display: 'flex', flexDirection: 'row', width: '100%' }
                    : { width: '100%', marginRight: '2rem' },
                ]}
              >
                {!showAttachment && (
                  <Typography variant="subtitle1" sx={{ color: 'black', marginBottom: '1rem' }}>
                    Request Details
                  </Typography>
                )}
                <Box
                  sx={{
                    marginTop: '1.5rem',
                    marginBottom: '2rem',
                    marginRight: '1rem',
                    padding: '1rem',
                    border: `1px solid ${theme.palette.border}`,
                    width: '100%',
                  }}
                >
                  {showAttachment && (
                    <Typography variant="subtitle1" sx={{ color: 'black', marginBottom: '1rem' }}>
                      Request Details
                    </Typography>
                  )}

                  {startDate && endDate && (
                    <>
                      <Typography
                        sx={{
                          marginBottom: '0.5rem',
                          color: 'black',
                          fontWeight: '300',
                          fontSize: '0.875rem',
                        }}
                        variant="body1"
                      >
                        Start date / End date
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                      >
                        {formatDate(startDate)} / {formatDate(endDate)}
                      </Typography>
                    </>
                  )}
                  <Typography
                    sx={{
                      marginBottom: '0.5rem',
                      color: 'black',
                      fontWeight: '300',
                      fontSize: '0.875rem',
                    }}
                    variant="body1"
                  >
                    Link
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                  >
                    {isHttpUrl(link) ? (
                      <Link
                        underline="always"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link}
                      </Link>
                    ) : (
                      link
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      marginBottom: '0.5rem',
                      color: 'black',
                      fontWeight: '300',
                      fontSize: '0.875rem',
                    }}
                    variant="body1"
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: '1rem', fontWeight: '400', color: 'black' }}
                  >
                    {description}
                  </Typography>
                </Box>
                {showAttachment && (
                  <Box
                    sx={{
                      marginTop: '1.5rem',
                      padding: '1.5rem 1rem',
                      border: `1px solid ${theme.palette.border}`,
                      width: '30rem',
                      marginBottom: '2rem',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        borderBottom: `1px solid ${theme.palette.border}`,
                        marginBottom: '1rem',
                        paddingBottom: '1rem',
                      }}
                    >
                      Attachment
                    </Typography>
                    {budgetCosts[0].invoiceIds.length === 0 && status !== 'approved' && (
                      <Typography variant="body2" sx={{ color: ' #979797' }}>
                        No files uploaded
                      </Typography>
                    )}
                    <InvoiceUpload budgetCostName="Bill" eduBudgetRequestId={id} status={status} />
                  </Box>
                )}
              </Box>
            </Box>
            {(type === 'Conference' || type === 'Workshop') && (
              <BudgetCosts
                budgetCosts={budgetCosts}
                eduBudgetRequestId={id}
                eduBudgetRequestStatus={status}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const EduBudgetRequestsTable = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user, getToken } = useAuth();
  const database = useFirestore();

  const loadRequests = useCallback(() => {
    const q = query(
      collection(database, 'edu_budget_requests'),
      where('userId', '==', user.uid),
      orderBy('requestedAt', 'desc'),
    );

    return getDocs(q)
      .then(snapshot => {
        const result = snapToArray(snapshot);
        setRequests(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [database, user.uid]);

  useEffect(() => {
    setLoading(true);
    loadRequests();
  }, [loadRequests]);

  const updateRequestStatus = useCallback(
    async (requestId, status) => {
      const result = await apiClient('POST', `/requests/edu-budget/${requestId}/status`, getToken, {
        status,
      });

      if (result?.status !== 'OK') {
        throw new Error(result?.message || 'Request failed');
      }

      loadRequests();
    },
    [getToken, loadRequests],
  );

  const cancelRequest = useCallback(
    async id => {
      try {
        await updateRequestStatus(id, 'canceled');

        enqueueSnackbar('Education budget request successfully canceled', {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar('Error when canceling education budget request', {
          variant: 'error',
        });
        console.error(err);
      }
    },
    [updateRequestStatus, enqueueSnackbar],
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hideActionsColumnAndCell = requests =>
    requests.filter(request => request.status === 'pending' || request.status === 'approved')
      .length > 0;

  return (
    <Portlet
      sx={{ background: 'none', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      {requests.length ? (
        <PortletHeader noDivider>
          <PortletLabel
            subtitle={`${requests.length} in total`}
            title="Latest Requests"
            sx={{
              '@media (max-width:480px)': {
                '& h5': {
                  fontSize: '1.3rem',
                },
                '& h6': {
                  marginLeft: '0.5rem',
                  fontSize: '0.7rem',
                  width: '3rem',
                },
              },
            }}
          />
        </PortletHeader>
      ) : (
        ''
      )}
      <PerfectScrollbar>
        <PortletContent
          isLoading={isLoading}
          sx={{ minWidth: '600px', overflow: 'auto' }}
          noPadding
        >
          {requests.length ? (
            <>
              <Table stickyHeader style={{ borderSpacing: '0px 0px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Status</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Title</TableCell>
                    {hideActionsColumnAndCell(requests) && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {requests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      ({
                        id,
                        description,
                        type,
                        title,
                        link,
                        requestedAt,
                        status = 'pending',
                        totalAmount,
                        startDate,
                        endDate,
                        budgetCosts,
                      }) => (
                        <Row
                          id={id}
                          description={description}
                          type={type}
                          title={title}
                          link={link}
                          requestedAt={requestedAt}
                          status={status}
                          totalAmount={totalAmount}
                          cancelRequest={cancelRequest}
                          hideActionsColumnAndCell={hideActionsColumnAndCell}
                          key={id}
                          requests={requests}
                          startDate={startDate}
                          endDate={endDate}
                          budgetCosts={budgetCosts}
                        />
                      ),
                    )}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={requests.length}
                page={page}
              />
            </>
          ) : (
            <Box sx={{ padding: '24px', paddingTop: 0, fontWeight: 300, color: '#777' }}>
              No education budget requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default EduBudgetRequestsTable;
